import { apiClient } from '@/lib/api-client';

export type CronJobRunStatus = 'running' | 'succeeded' | 'failed';
export type StatusLogActorType =
  | 'admin'
  | 'cron'
  | 'webhook'
  | 'payment'
  | 'system';

export interface CronRunRow {
  runId: string;
  jobName: string;
  tenantId: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: CronJobRunStatus;
  counters: Record<string, number>;
  errorMessage: string | null;
  notes: string | null;
}

export interface CronRunsReport {
  today: string;
  daysWindow: number;
  total: number;
  runs: CronRunRow[];
}

export interface CronJobStatRow {
  jobName: string;
  totalRuns: number;
  succeeded: number;
  failed: number;
  running: number;
  successRate: number;
  avgDurationMs: number | null;
  lastRunAt: string | null;
  lastRunStatus: CronJobRunStatus | null;
  totalProcessed: number;
}

export interface CronStatsReport {
  today: string;
  daysWindow: number;
  jobs: CronJobStatRow[];
}

export interface UserStatusChangeRow {
  logId: string;
  userId: string;
  name: string;
  surname: string;
  email: string | null;
  phone: string | null;
  from: 'active' | 'inactive' | null;
  to: 'active' | 'inactive' | 'deleted';
  reason: string;
  actorType: StatusLogActorType;
  actorId: string | null;
  metadata: Record<string, any> | null;
  triggeredAt: string;
}

export interface UserStatusChangesReport {
  today: string;
  daysWindow: number;
  total: number;
  filteredByUserId: string | null;
  changes: UserStatusChangeRow[];
}

export const getCronRuns = async (opts: {
  days?: number;
  job?: string;
  status?: CronJobRunStatus;
  limit?: number;
}): Promise<CronRunsReport> => {
  const params: Record<string, string | number> = {};
  if (opts.days !== undefined) params.days = opts.days;
  if (opts.job) params.job = opts.job;
  if (opts.status) params.status = opts.status;
  if (opts.limit !== undefined) params.limit = opts.limit;
  const response = await apiClient.get('/cron-reports/runs', { params });
  return response.data as CronRunsReport;
};

export const getCronStats = async (days = 7): Promise<CronStatsReport> => {
  const response = await apiClient.get('/cron-reports/stats', {
    params: { days },
  });
  return response.data as CronStatsReport;
};

export const getUserStatusChanges = async (opts: {
  days?: number;
  limit?: number;
  userId?: string;
}): Promise<UserStatusChangesReport> => {
  const params: Record<string, string | number> = {};
  if (opts.days !== undefined) params.days = opts.days;
  if (opts.limit !== undefined) params.limit = opts.limit;
  if (opts.userId) params.userId = opts.userId;
  const response = await apiClient.get('/cron-reports/user-status-changes', {
    params,
  });
  return response.data as UserStatusChangesReport;
};
